import { useEffect, useMemo, useState } from "react";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { formatEuroIt } from "../lib/euro";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const Stat = ({ label, value, color, testId }) => (
  <div className="rounded-md border border-slate-200 bg-white px-3 py-2" data-testid={testId}>
    <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
    <div className={`text-lg font-semibold ${color}`}>{value}</div>
  </div>
);

const Matrix = ({ celle }) => {
  const { eta, punti, map } = useMemo(() => {
    const mp = new Map();
    const e = new Set(); const p = new Set();
    celle.forEach(([a, b, v]) => { mp.set(`${a}|${b}`, v); e.add(a); p.add(b); });
    return { eta: [...e].sort((x, y) => x - y), punti: [...p].sort((x, y) => x - y), map: mp };
  }, [celle]);
  return (
    <div className="max-h-[340px] overflow-auto rounded border border-slate-200" data-testid="tinv-preview-matrix">
      <table className="text-xs border-collapse">
        <thead className="sticky top-0 bg-slate-100 z-10">
          <tr>
            <th className="sticky left-0 bg-slate-200 px-2 py-1 text-left">Età \ Punti</th>
            {punti.map((p) => <th key={p} className="px-2 py-1 text-right font-semibold whitespace-nowrap">{p}%</th>)}
          </tr>
        </thead>
        <tbody>
          {eta.map((a) => (
            <tr key={a} className="odd:bg-white even:bg-slate-50">
              <td className="sticky left-0 bg-slate-100 px-2 py-0.5 font-semibold">{a}</td>
              {punti.map((p) => <td key={p} className="px-2 py-0.5 text-right font-mono whitespace-nowrap">{map.has(`${a}|${p}`) ? formatEuroIt(map.get(`${a}|${p}`)) : "—"}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Anteprima e conferma dell'importazione di una tabella micro/macro invalidità.
export const TabellaInvaliditaPreview = ({ tipo, data, onClose, onDone }) => {
  const [anno, setAnno] = useState(data.anno || "");
  const [pb, setPb] = useState(data.punto_base ?? "");
  const [itt, setItt] = useState(data.itt_giornaliero ?? "");
  const [cmp, setCmp] = useState(data.confronto || null);
  const [sost, setSost] = useState(false);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);

  useEffect(() => {
    if (!/^\d{4}$/.test(anno)) { setCmp(null); return; }
    const t = setTimeout(() => {
      api.post(`/admin/tabelle-invalidita/${tipo}/confronto`, { anno, celle: data.celle }).then(({ data: d }) => setCmp(d)).catch(() => setCmp(null));
    }, 300);
    return () => clearTimeout(t);
  }, [anno, tipo, data.celle]);

  const confirm = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post(`/admin/tabelle-invalidita/${tipo}/importa`, { anno, celle: data.celle, punto_base: pb, itt_giornaliero: itt, sostituisci: sost });
      setRes(d);
      toast.success(`Tabella ${tipo.toUpperCase()} ${anno} importata`);
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-5xl sm:max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="tinv-preview-dialog">
        <DialogHeader><DialogTitle>{res ? "Riepilogo importazione" : "Anteprima"} · Tabella {tipo === "micro" ? "MICRO" : "MACRO"} invalidità</DialogTitle></DialogHeader>
        {res ? (
          <div className="space-y-3" data-testid="tinv-result">
            <div className="flex items-center gap-2 text-emerald-700 font-semibold"><CheckCircle2 className="h-5 w-5" />Importazione completata · anno {res.anno}</div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              <Stat label="Nuove righe" value={res.nuove} color="text-emerald-700" testId="tinv-res-nuove" />
              <Stat label="Aggiornate" value={res.aggiornate} color="text-amber-600" testId="tinv-res-aggiornate" />
              <Stat label="Invariate" value={res.invariate} color="text-slate-700" testId="tinv-res-invariate" />
              <Stat label="Rimosse" value={res.rimosse} color="text-red-600" testId="tinv-res-rimosse" />
              <Stat label="Totale in tabella" value={res.totale} color="text-slate-900" testId="tinv-res-totale" />
            </div>
            {res.scartate > 0 && <div className="text-xs text-amber-700">{res.scartate} valori scartati perché non validi.</div>}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
              <label className="space-y-1"><span className="text-xs text-slate-500">Anno tabella *</span>
                <Input value={anno} onChange={(e) => setAnno(e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="es. 2026" data-testid="tinv-preview-anno" /></label>
              <label className="space-y-1"><span className="text-xs text-slate-500">Punto base € (facoltativo)</span>
                <Input value={pb ?? ""} onChange={(e) => setPb(e.target.value)} data-testid="tinv-preview-pb" /></label>
              <label className="space-y-1"><span className="text-xs text-slate-500">Indennità giornaliera € (facoltativo)</span>
                <Input value={itt ?? ""} onChange={(e) => setItt(e.target.value)} data-testid="tinv-preview-itt" /></label>
            </div>
            {(data.avvisi || []).map((a, i) => <div key={i} className="rounded bg-amber-50 border border-amber-200 px-3 py-1.5 text-xs text-amber-800" data-testid={`tinv-preview-warn-${i}`}>{a}</div>)}
            <div className="text-sm text-slate-600" data-testid="tinv-preview-count">{data.celle.length} valori riconosciuti</div>
            {cmp && (
              <div className="grid grid-cols-3 gap-2" data-testid="tinv-preview-compare">
                <Stat label="Nuove" value={cmp.nuove} color="text-emerald-700" testId="tinv-cmp-nuove" />
                <Stat label="Da aggiornare" value={cmp.aggiornate} color="text-amber-600" testId="tinv-cmp-aggiornate" />
                <Stat label="Invariate" value={cmp.invariate} color="text-slate-700" testId="tinv-cmp-invariate" />
              </div>
            )}
            <Matrix celle={data.celle} />
            {cmp?.tabella_esistente && (
              <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={sost} onChange={(e) => setSost(e.target.checked)} data-testid="tinv-preview-sostituisci" className="h-4 w-4 accent-red-600" />
                Sostituisci tutta la tabella {anno} (i valori non presenti in questo caricamento verranno eliminati)
              </label>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="tinv-preview-close">{res ? "Chiudi" : "Annulla"}</Button>
          {!res && <Button onClick={confirm} disabled={busy || !/^\d{4}$/.test(anno) || !data.celle.length} data-testid="tinv-preview-confirm" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Conferma importazione"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
